import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme } from 'antd';
import App from './App';
import { store } from './app/store';
import { ThemeProvider, useTheme } from './theme/ThemeProvider';
import { BRAND, SEMANTIC, SURFACE, RADIUS, FONT_FAMILY } from './theme/tokens';
import './index.css';

// KaziPeople shared design tokens for Ant Design (mode-independent), sourced from the
// single token module so brand/status colour never drifts between CSS and Ant.
const baseToken = {
  colorPrimary: BRAND.primary,
  colorLink: BRAND.primary,
  colorSuccess: SEMANTIC.success,
  colorWarning: SEMANTIC.warning,
  colorError: SEMANTIC.danger,
  colorInfo: SEMANTIC.info,
  borderRadius: RADIUS,
  fontFamily: FONT_FAMILY,
};

// Switches antd between light/dark algorithms and surface colors with the theme.
const ThemedAntd: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          ...baseToken,
          colorBgLayout: isDark ? SURFACE.dark.layout : SURFACE.light.layout,
          colorBgContainer: isDark ? SURFACE.dark.container : SURFACE.light.container,
        },
        components: {
          // Sidebar selected item = solid orange pill + white label (readable in
          // both themes), instead of the orange-on-orange that was unreadable.
          Menu: {
            darkItemSelectedBg: BRAND.primary,
            darkItemSelectedColor: '#FFFFFF',
            darkItemHoverColor: '#FFFFFF',
            itemSelectedBg: BRAND.primary,
            itemSelectedColor: '#FFFFFF',
            itemHoverColor: BRAND.primaryHover,
            itemBorderRadius: RADIUS,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <Provider store={store}>
        <BrowserRouter>
          <ThemedAntd>
            <App />
          </ThemedAntd>
        </BrowserRouter>
      </Provider>
    </ThemeProvider>
  </React.StrictMode>
);
